import { addMembre, addRelation } from '../api';

const MONTHS: Record<string, string> = {
  JAN:'01',FEB:'02',MAR:'03',APR:'04',MAY:'05',JUN:'06',
  JUL:'07',AUG:'08',SEP:'09',OCT:'10',NOV:'11',DEC:'12',
};

function parseDate(val: string): string | undefined {
  const clean = val.trim().toUpperCase().replace(/^(ABT|BEF|AFT|EST|CAL|CIRCA|CA)\s+/,'');
  const p = clean.split(/\s+/);
  if (p.length >= 3) {
    const m = MONTHS[p[1]] || '01';
    return `${p[2]}-${m}-${p[0].padStart(2,'0')}`;
  }
  if (p.length === 2) {
    const m = MONTHS[p[0]];
    if (m) return `${p[1]}-${m}-01`;
  }
  if (p.length === 1 && /^\d{3,4}$/.test(p[0])) return `${p[0]}-01-01`;
  return undefined;
}

function parseName(val: string) {
  const m = val.match(/^(.*?)\s*\/([^/]*)\//);
  if (m) {
    const firstName = m[1].trim();
    const lastName = m[2].trim();
    return { firstName: firstName || lastName, lastName: firstName ? lastName : '' };
  }
  const parts = val.trim().split(/\s+/);
  return { firstName: parts.slice(0, -1).join(' ') || parts[0], lastName: parts.slice(-1)[0] || '' };
}

interface Indi {
  id: string; firstName: string; lastName: string; maidenName?: string;
  sex?: string; birthDate?: string; birthPlace?: string;
  deathDate?: string; deathPlace?: string; notes?: string;
}
interface Fam {
  id: string; husb?: string; wife?: string; children: string[];
}

export interface GedcomParseResult {
  membres: number;    // count
  familles: number;
  individuals: Indi[];
  families: Fam[];
}

export function parseGedcom(content: string): GedcomParseResult {
  const lines = content.split(/\r?\n/);
  const individuals: Indi[] = [];
  const families: Fam[] = [];

  let currentIndi: Indi | null = null;
  let currentFam: Fam | null = null;
  let context = '';    // 'BIRT' | 'DEAT' | 'NAME' | ''
  let level1tag = '';

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    const m = line.match(/^(\d+)\s+(@[^@]+@)?\s*(\w+)\s*(.*)?$/);
    if (!m) continue;
    const level = parseInt(m[1]);
    const xref  = m[2]?.replace(/@/g,'').trim();
    const tag   = m[3];
    const val   = (m[4] || '').trim();

    if (level === 0) {
      if (currentIndi) individuals.push(currentIndi);
      if (currentFam)  families.push(currentFam);
      currentIndi = null; currentFam = null; context = ''; level1tag = '';

      if (tag === 'INDI' && xref) {
        currentIndi = { id: xref, firstName: '', lastName: '' };
      } else if (tag === 'FAM' && xref) {
        currentFam = { id: xref, children: [] };
      }
      continue;
    }

    if (currentIndi) {
      if (level === 1) { level1tag = tag; context = tag; }
      if (level === 1 && tag === 'NAME') {
        const parsed = parseName(val);
        currentIndi.firstName = parsed.firstName;
        currentIndi.lastName  = parsed.lastName;
      }
      if (level === 1 && tag === 'SEX')  currentIndi.sex = val === 'M' ? 'Homme' : val === 'F' ? 'Femme' : undefined;
      if (level === 1 && tag === 'NOTE') currentIndi.notes = val;
      if (level === 2) {
        if (context === 'BIRT') {
          if (tag === 'DATE') currentIndi.birthDate  = parseDate(val);
          if (tag === 'PLAC') currentIndi.birthPlace = val;
        }
        if (context === 'DEAT') {
          if (tag === 'DATE') currentIndi.deathDate  = parseDate(val);
          if (tag === 'PLAC') currentIndi.deathPlace = val;
        }
        if (context === 'NAME' && tag === '_MARNM') currentIndi.maidenName = val;
      }
    }

    if (currentFam) {
      if (level === 1) {
        if (tag === 'HUSB') currentFam.husb = val.replace(/@/g,'');
        if (tag === 'WIFE') currentFam.wife = val.replace(/@/g,'');
        if (tag === 'CHIL') currentFam.children.push(val.replace(/@/g,''));
      }
    }
  }
  if (currentIndi) individuals.push(currentIndi);
  if (currentFam)  families.push(currentFam);

  return { membres: individuals.length, familles: families.length, individuals, families };
}

export async function importGedcom(
  result: GedcomParseResult,
  onProgress?: (msg: string) => void,
): Promise<{ membres: number; relations: number }> {
  const gedIdToDbId = new Map<string, number>();

  onProgress?.(`Création de ${result.membres} membres…`);
  for (const indi of result.individuals) {
    try {
      const payload: any = {
        prenom: indi.firstName || '?',
        nom:    indi.lastName  || '?',
      };
      if (indi.maidenName)  payload.nom_jeune_fille = indi.maidenName;
      if (indi.sex)         payload.sexe            = indi.sex;
      if (indi.birthDate)   payload.date_naissance  = indi.birthDate;
      if (indi.birthPlace)  payload.lieu_naissance  = indi.birthPlace;
      if (indi.deathDate)   payload.date_deces      = indi.deathDate;
      if (indi.deathPlace)  payload.lieu_deces      = indi.deathPlace;
      if (indi.notes)       payload.notes           = indi.notes;
      const created = await addMembre(payload);
      gedIdToDbId.set(indi.id, created.id);
    } catch { /* continue */ }
  }

  let relCount = 0;
  onProgress?.(`Création des relations…`);
  for (const fam of result.families) {
    const husbId = fam.husb ? gedIdToDbId.get(fam.husb) : undefined;
    const wifeId = fam.wife ? gedIdToDbId.get(fam.wife) : undefined;

    // Relation conjugale
    if (husbId && wifeId) {
      try {
        await addRelation({ membre_source: husbId, membre_cible: wifeId, type_relation: 'conjoint' });
        relCount++;
      } catch { /* continue */ }
    }

    // Filiations
    for (const childGedId of fam.children) {
      const childId = gedIdToDbId.get(childGedId);
      if (!childId) continue;
      for (const parentId of [husbId, wifeId]) {
        if (!parentId) continue;
        try {
          await addRelation({ membre_source: parentId, membre_cible: childId, type_relation: 'parent' });
          relCount++;
        } catch { /* continue */ }
      }
    }
  }

  return { membres: gedIdToDbId.size, relations: relCount };
}
